import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, message, Popconfirm, Tag, Statistic, Row, Col, Space, Switch, Empty, Spin,
} from 'antd';
import {
  UserOutlined, ProjectOutlined, DatabaseOutlined, DashboardOutlined,
  DeleteOutlined, KeyOutlined, CrownOutlined, ReloadOutlined,
  TeamOutlined, FileTextOutlined, ShareAltOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import api from '../services/auth';

const AdminPage = ({ isMobile }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [recordsData, setRecordsData] = useState({ records: [], total: 0, page: 1, limit: 20 });
  const [stats, setStats] = useState(null);

  const [resetPwdVisible, setResetPwdVisible] = useState(false);
  const [resetPwdTarget, setResetPwdTarget] = useState(null);
  const [resetPwdForm] = Form.useForm();

  useEffect(() => {
    checkAdmin();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAdmin = async () => {
    try {
      const res = await api.get('/admin/check');
      setIsAdmin(res.data.isAdmin);
      if (res.data.isAdmin) {
        loadStats();
      }
    } catch {
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      console.error('加载统计失败:', err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch (err) {
      message.error('加载用户列表失败');
    }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const res = await api.get('/admin/projects');
      setProjects(res.data);
    } catch (err) {
      message.error('加载项目列表失败');
    }
  }, []);

  const loadRecords = useCallback(async (page = 1) => {
    try {
      const res = await api.get('/admin/records', { params: { page, limit: 20 } });
      setRecordsData(res.data);
    } catch (err) {
      message.error('加载记录列表失败');
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    if (activeTab === 'overview') loadStats();
    else if (activeTab === 'users') loadUsers();
    else if (activeTab === 'projects') loadProjects();
    else if (activeTab === 'records') loadRecords(recordsData.page);
  }, [activeTab, isAdmin, loadStats, loadUsers, loadProjects, loadRecords, recordsData.page]);

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}/admin`, { isAdmin: !currentStatus });
      message.success(!currentStatus ? '已设为管理员' : '已取消管理员');
      loadUsers();
    } catch (err) {
      message.error(err.response?.data?.error || '操作失败');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      message.success('用户已删除');
      loadUsers();
      loadStats();
    } catch (err) {
      message.error(err.response?.data?.error || '删除失败');
    }
  };

  const handleResetPassword = async (values) => {
    try {
      await api.put(`/admin/users/${resetPwdTarget.id}/reset-password`, { password: values.password });
      message.success('密码已重置');
      setResetPwdVisible(false);
      setResetPwdTarget(null);
      resetPwdForm.resetFields();
    } catch (err) {
      message.error(err.response?.data?.error || '重置失败');
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await api.delete(`/admin/projects/${projectId}`);
      message.success('项目已删除');
      loadProjects();
      loadStats();
    } catch (err) {
      message.error(err.response?.data?.error || '删除失败');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    try {
      await api.delete(`/admin/records/${recordId}`);
      message.success('记录已删除');
      loadRecords(recordsData.page);
      loadStats();
    } catch (err) {
      message.error(err.response?.data?.error || '删除失败');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: '#86868b' }}>
              <CrownOutlined style={{ fontSize: 32, color: '#ff9500', display: 'block', marginBottom: 12 }} />
              需要管理员权限才能访问此页面
            </span>
          }
        />
      </div>
    );
  }

  const cardStyle = {
    borderRadius: 16,
    border: '1px solid rgba(0,0,0,0.06)',
    background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  };

  const statCardStyle = {
    ...cardStyle,
    textAlign: 'center',
    padding: isMobile ? 16 : 24,
  };

  const tabItems = [
    { key: 'overview', label: <span><DashboardOutlined /> 概览</span> },
    { key: 'users', label: <span><UserOutlined /> 用户</span> },
    { key: 'projects', label: <span><ProjectOutlined /> 项目</span> },
    { key: 'records', label: <span><DatabaseOutlined /> 数据</span> },
  ];

  const renderOverview = () => (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={6}>
          <div style={statCardStyle}>
            <TeamOutlined style={{ fontSize: 28, color: '#007AFF', marginBottom: 8 }} />
            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: '#1d1d1f', letterSpacing: -1 }}>
              {stats?.users ?? '-'}
            </div>
            <div style={{ fontSize: 13, color: '#86868b', fontWeight: 500, marginTop: 4 }}>用户总数</div>
          </div>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <div style={statCardStyle}>
            <ProjectOutlined style={{ fontSize: 28, color: '#34C759', marginBottom: 8 }} />
            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: '#1d1d1f', letterSpacing: -1 }}>
              {stats?.projects ?? '-'}
            </div>
            <div style={{ fontSize: 13, color: '#86868b', fontWeight: 500, marginTop: 4 }}>项目总数</div>
          </div>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <div style={statCardStyle}>
            <FileTextOutlined style={{ fontSize: 28, color: '#FF9500', marginBottom: 8 }} />
            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: '#1d1d1f', letterSpacing: -1 }}>
              {stats?.records ?? '-'}
            </div>
            <div style={{ fontSize: 13, color: '#86868b', fontWeight: 500, marginTop: 4 }}>记录总数</div>
          </div>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <div style={statCardStyle}>
            <ShareAltOutlined style={{ fontSize: 28, color: '#AF52DE', marginBottom: 8 }} />
            <div style={{ fontSize: isMobile ? 28 : 36, fontWeight: 700, color: '#1d1d1f', letterSpacing: -1 }}>
              {stats?.shares ?? '-'}
            </div>
            <div style={{ fontSize: 13, color: '#86868b', fontWeight: 500, marginTop: 4 }}>分享总数</div>
          </div>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card style={cardStyle} size="small">
            <Statistic
              title={<span style={{ color: '#86868b', fontWeight: 500 }}>7天内活跃天数</span>}
              value={stats?.activeDays7d ?? '-'}
              suffix="/ 7"
              valueStyle={{ fontWeight: 700, color: '#1d1d1f' }}
              prefix={<ThunderboltOutlined style={{ color: '#FFCC00' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card style={cardStyle} size="small">
            <Statistic
              title={<span style={{ color: '#86868b', fontWeight: 500 }}>24小时活跃用户</span>}
              value={stats?.activeUsers1d ?? '-'}
              suffix={`/ ${stats?.users ?? 0}`}
              valueStyle={{ fontWeight: 700, color: '#1d1d1f' }}
              prefix={<UserOutlined style={{ color: '#007AFF' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card style={cardStyle} size="small">
            <Statistic
              title={<span style={{ color: '#86868b', fontWeight: 500 }}>7天平均记录间隔</span>}
              value={stats?.avgInterval7d != null ? stats.avgInterval7d : '-'}
              suffix="分钟"
              valueStyle={{ fontWeight: 700, color: '#1d1d1f' }}
              prefix={<ThunderboltOutlined style={{ color: '#FF9500' }} />}
            />
          </Card>
        </Col>
      </Row>
      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Button icon={<ReloadOutlined />} onClick={loadStats} type="text" style={{ color: '#86868b' }}>
          刷新数据
        </Button>
      </div>
    </div>
  );

  const userColumns = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <span>
          {text}
          {record.is_admin && <Tag color="gold" style={{ marginLeft: 6, borderRadius: 8, fontSize: 11 }}><CrownOutlined /> 管理员</Tag>}
        </span>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'created_at',
      key: 'created_at',
      responsive: ['md'],
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: isMobile ? 120 : 200,
      render: (_, record) => (
        <Space size={4} wrap>
          <Switch
            size="small"
            checked={!!record.is_admin}
            onChange={() => handleToggleAdmin(record.id, record.is_admin)}
            checkedChildren={<CrownOutlined />}
            unCheckedChildren="设管"
          />
          <Button
            type="text"
            size="small"
            icon={<KeyOutlined />}
            onClick={() => { setResetPwdTarget(record); resetPwdForm.resetFields(); setResetPwdVisible(true); }}
            title="重置密码"
          />
          <Popconfirm title="确定删除该用户及其所有数据？" onConfirm={() => handleDeleteUser(record.id)}>
            <Button type="text" size="small" danger icon={<DeleteOutlined />} title="删除" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const projectColumns = [
    {
      title: '项目名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '所有者',
      dataIndex: 'owner_name',
      key: 'owner_name',
      responsive: ['sm'],
    },
    {
      title: '记录数',
      dataIndex: 'record_count',
      key: 'record_count',
      width: 80,
      render: (v) => <Tag color="blue" style={{ borderRadius: 8 }}>{v}</Tag>,
    },
    {
      title: '分享数',
      dataIndex: 'share_count',
      key: 'share_count',
      width: 80,
      responsive: ['md'],
      render: (v) => <Tag color="purple" style={{ borderRadius: 8 }}>{v}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      responsive: ['lg'],
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 60,
      render: (_, record) => (
        <Popconfirm title="确定删除该项目及其所有记录？" onConfirm={() => handleDeleteProject(record.id)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} title="删除" />
        </Popconfirm>
      ),
    },
  ];

  const recordColumns = [
    {
      title: '时间',
      dataIndex: 'recorded_at',
      key: 'recorded_at',
      width: isMobile ? 100 : 170,
      render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-',
    },
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      responsive: ['sm'],
    },
    {
      title: '项目',
      dataIndex: 'project_name',
      key: 'project_name',
      responsive: ['md'],
    },
    {
      title: '路径',
      dataIndex: 'node_path',
      key: 'node_path',
      ellipsis: true,
    },
    {
      title: '数值',
      key: 'numeric',
      width: 90,
      responsive: ['md'],
      render: (_, record) => {
        if (record.numeric_value != null) {
          return <Tag color="green" style={{ borderRadius: 8 }}>{record.numeric_value}{record.numeric_unit || ''}</Tag>;
        }
        return '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 60,
      render: (_, record) => (
        <Popconfirm title="确定删除该记录？" onConfirm={() => handleDeleteRecord(record.id)}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} title="删除" />
        </Popconfirm>
      ),
    },
  ];

  const renderUsers = () => (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#86868b', fontSize: 13, fontWeight: 500 }}>共 {users.length} 个用户</span>
        <Button icon={<ReloadOutlined />} onClick={loadUsers} type="text" size="small" style={{ color: '#86868b' }}>
          刷新
        </Button>
      </div>
      <Card style={cardStyle} size="small" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={users}
          columns={userColumns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: false, simple: isMobile }}
          scroll={isMobile ? { x: 300 } : undefined}
        />
      </Card>
    </div>
  );

  const renderProjects = () => (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#86868b', fontSize: 13, fontWeight: 500 }}>共 {projects.length} 个项目</span>
        <Button icon={<ReloadOutlined />} onClick={loadProjects} type="text" size="small" style={{ color: '#86868b' }}>
          刷新
        </Button>
      </div>
      <Card style={cardStyle} size="small" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={projects}
          columns={projectColumns}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: false, simple: isMobile }}
          scroll={isMobile ? { x: 300 } : undefined}
        />
      </Card>
    </div>
  );

  const renderRecords = () => (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#86868b', fontSize: 13, fontWeight: 500 }}>
          共 {recordsData.total} 条记录
        </span>
        <Button icon={<ReloadOutlined />} onClick={() => loadRecords(recordsData.page)} type="text" size="small" style={{ color: '#86868b' }}>
          刷新
        </Button>
      </div>
      <Card style={cardStyle} size="small" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={recordsData.records}
          columns={recordColumns}
          rowKey="id"
          size="small"
          pagination={{
            current: recordsData.page,
            pageSize: recordsData.limit,
            total: recordsData.total,
            showSizeChanger: false,
            simple: isMobile,
            onChange: (page) => loadRecords(page),
          }}
          scroll={isMobile ? { x: 300 } : undefined}
        />
      </Card>
    </div>
  );

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <CrownOutlined style={{ fontSize: 24, color: '#ff9500' }} />
        <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#1d1d1f', letterSpacing: -0.5 }}>
          管理后台
        </h2>
      </div>

      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 20,
        overflowX: 'auto',
        paddingBottom: 4,
      }}>
        {tabItems.map(item => (
          <Button
            key={item.key}
            type={activeTab === item.key ? 'primary' : 'default'}
            onClick={() => setActiveTab(item.key)}
            style={{
              borderRadius: 20,
              fontWeight: 500,
              whiteSpace: 'nowrap',
              background: activeTab === item.key ? '#007AFF' : 'rgba(255,255,255,0.8)',
              borderColor: activeTab === item.key ? '#007AFF' : 'rgba(0,0,0,0.1)',
            }}
          >
            {item.label}
          </Button>
        ))}
      </div>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'users' && renderUsers()}
      {activeTab === 'projects' && renderProjects()}
      {activeTab === 'records' && renderRecords()}

      <Modal
        title={<span><KeyOutlined style={{ color: '#ff9500', marginRight: 8 }} />重置密码 — {resetPwdTarget?.username}</span>}
        open={resetPwdVisible}
        onCancel={() => { setResetPwdVisible(false); setResetPwdTarget(null); }}
        footer={null}
        width={isMobile ? '95%' : 400}
      >
        <Form form={resetPwdForm} onFinish={handleResetPassword} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="password" label="新密码" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '密码至少6位' }]}>
            <Input.Password placeholder="输入新密码" autoFocus />
          </Form.Item>
          <Form.Item name="confirm" label="确认密码" dependencies={['password']} rules={[
            { required: true, message: '请确认密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) return Promise.resolve();
                return Promise.reject(new Error('两次密码不一致'));
              },
            }),
          ]}>
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block style={{ borderRadius: 12 }}>
              确认重置
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPage;
